import { useEffect, useState } from 'react'

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Section } from '@factorialco/deprecated-design-system'
import { F0Box, F0Text } from '@factorialco/f0-react'
import {
  SalaryAdvanceSettingsPageDocument,
  SaveSalaryAdvanceCompanySettingDocument,
  type SaveSalaryAdvanceCompanySettingMutation,
} from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  MAX_REQUEST_WINDOW_DAY,
  MIN_REQUEST_WINDOW_DAY,
  normalizeCapPercentage,
} from 'modules/salary_advance/lib/companySettings'

type Props = {
  requestWindowStartDay: number
  requestDeadlineDay: number
  serviceEnabled: boolean
  advanceCapMonthlyWagePercent: number | null | undefined
  advanceCapFixedAmountCents: number | null
  canEdit: boolean
}

const RequestWindowSetting = ({
  requestWindowStartDay,
  requestDeadlineDay,
  serviceEnabled,
  advanceCapMonthlyWagePercent,
  advanceCapFixedAmountCents,
  canEdit,
}: Props) => {
  const [selectedStartDay, setSelectedStartDay] = useState<number>(requestWindowStartDay)
  const [selectedDeadlineDay, setSelectedDeadlineDay] = useState<number>(requestDeadlineDay)

  const [saveCompanySetting, { loading }] = useMutation(SaveSalaryAdvanceCompanySettingDocument, {
    extractorFn: (payload: SaveSalaryAdvanceCompanySettingMutation) =>
      payload.salaryAdvanceMutations?.saveSalaryadvanceCompanySetting ?? { errors: [] },
    refetchQueries: [SalaryAdvanceSettingsPageDocument],
  })

  useEffect(() => {
    setSelectedStartDay(requestWindowStartDay)
  }, [requestWindowStartDay])

  useEffect(() => {
    setSelectedDeadlineDay(requestDeadlineDay)
  }, [requestDeadlineDay])

  const isDirty =
    selectedStartDay !== requestWindowStartDay || selectedDeadlineDay !== requestDeadlineDay

  const handleSave = async () => {
    const result = await saveCompanySetting({
      variables: {
        requestWindowStartDay: selectedStartDay,
        requestDeadlineDay: selectedDeadlineDay,
        serviceEnabled,
        advanceCapMonthlyWagePercent: normalizeCapPercentage(advanceCapMonthlyWagePercent),
        advanceCapFixedAmountCents,
      },
    })
    const handledResult = handleResult(result, () => {})

    track(trackingEvents.salaryAdvance.settingsSaveResult, {
      setting_key: 'request_window',
      setting_value_start_day: selectedStartDay,
      setting_value_deadline_day: selectedDeadlineDay,
      outcome: handledResult.success ? 'success' : 'failure',
    })

    if (handledResult.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  const dayOptions = Array.from(
    { length: MAX_REQUEST_WINDOW_DAY - MIN_REQUEST_WINDOW_DAY + 1 },
    (_, i) => ({
      label: i18n.t('salary_advance.settings.request_window.day_of_each_month', {
        day: MIN_REQUEST_WINDOW_DAY + i,
      }),
      value: MIN_REQUEST_WINDOW_DAY + i,
    })
  )

  const handleDayChange = (setter: (v: number) => void) => (newValue: string) => {
    const nextValue = Number(newValue)
    if (!Number.isNaN(nextValue)) setter(nextValue)
  }

  return (
    <Section.Form
      illustration="clock-app"
      title={i18n.t('salary_advance.settings.request_window.title')}
      description={i18n.t('salary_advance.settings.request_window.description')}
      submitLabel={i18n.t('common.save')}
      isDirty={isDirty}
      onSubmit={handleSave}
      disabled={!canEdit || loading}
    >
      <F0Box display="flex" flexDirection="row" flexWrap="wrap" gap="md">
        <F0Box grow minWidth="48">
          <F0Text
            variant="label"
            content={i18n.t('salary_advance.settings.request_window.start_day_label')}
          />
          <Select
            none={i18n.t('common.select')}
            value={String(selectedStartDay)}
            onChange={handleDayChange(setSelectedStartDay)}
            options={dayOptions.filter((o) => o.value < selectedDeadlineDay)}
            missingLabel={i18n.t('common.missing_field')}
            readonly={!canEdit}
          />
        </F0Box>
        <F0Box grow minWidth="48">
          <F0Text
            variant="label"
            content={i18n.t('salary_advance.settings.request_window.deadline_day_label')}
          />
          <Select
            none={i18n.t('common.select')}
            value={String(selectedDeadlineDay)}
            onChange={handleDayChange(setSelectedDeadlineDay)}
            options={dayOptions.filter((o) => o.value > selectedStartDay)}
            missingLabel={i18n.t('common.missing_field')}
            readonly={!canEdit}
          />
        </F0Box>
      </F0Box>
    </Section.Form>
  )
}

export default RequestWindowSetting
